import { SyntheticEvent, useState } from 'react';
import history from 'service/customHistory';
import { useRecoilValue } from 'recoil';
import Highcharts from 'highcharts';
import merge from 'lodash/merge';
import get from 'lodash/get';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { HoverData } from 'common/types';

import { getBaseGraphConfig } from 'components/common/Graphs/graphConfigs';
import TimelineLegend from 'components/common/Legends/TimelineLegend';
import ScalyrHighcharts from 'components/common/ScalyrHighcharts';
import { highchartsStylesState, plotsStylesState } from 'styles/stylesProvider';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { getTimezoneOffset } from 'common/util/time';

import './LogVolumeGraph.scss';

/**
 * Get link to the events page
 */
const getSearchPageLink = (startTime: string | number, endTime: string | number): string => {
  const searchParams = new URLSearchParams();
  if (startTime) {
    searchParams.append('startTime', encodeURIComponent(startTime));
  }
  if (endTime) {
    searchParams.append('endTime', encodeURIComponent(endTime));
  }
  return '/events?' + searchParams.toString();
};

/**
 * Log volume graph for the overview page. This use the time line legend to show
 * time range for the graph and the value of the hover.
 *
 * @param - Array[[Number, Number]] - Array of tuples containing [x,y] plot values for the graph
 * series.
 */
type LogVolumeGraphProps = {
  data: Array<[number | undefined, number | undefined]>;
  plotColor?: string;
};

function LogVolumeGraph({ data }: LogVolumeGraphProps): JSX.Element {
  const startTime = get(data, '[0][0]');
  const endTime = get(data, '[479][0]');
  const [hoverData, setHoverData] = useState<HoverData>({});
  const highchartsStyles = useRecoilValue(highchartsStylesState);
  const plotsStyles = useRecoilValue(plotsStylesState);
  const timezone = useRecoilValue(timezoneState);
  const timezoneOffset = getTimezoneOffset(timezone);

  const logVolumeGraphConfig = {
    yAxis: {
      gridLineWidth: 0.5,
      offset: 20,
      minorGridLineWidth: 0,
      tickAmount: 4,
      tickPixelInterval: 10,
      tickWidth: 0,
      lineWidth: 0,
      minPadding: 0.5,
      maxPadding: 0,
      startOnTick: false,
      endOnTick: false,
      labels: {
        align: 'left',
        x: 0,
        enabled: true,
      },
    },
    xAxis: {
      crosshair: {
        snap: false,
        width: 1.5,
      },
    },
    plotOptions: {
      global: {
        useUTC: !!timezone,
      },
      [K.SERIES_TYPES.LINE]: {
        color: plotsStyles[0],
        animation: false,
        marker: {
          enabled: false,
        },
        lineWidth: 1.5,
        states: {
          hover: {
            enabled: false,
            lineWidth: 1.5,
          },
        },
      },
      series: {
        point: {
          events: {
            click: (): void => {
              history.push(K.ROUTE.EVENTS);
            },
            mouseOver: (e: SyntheticEvent): void => {
              const target = e.target as typeof e.target & {
                x: number;
                y: number;
              };
              setHoverData({
                value: Math.round(target.y).toLocaleString() + ' bytes',
                time: Highcharts.dateFormat('%b %e · %l:%M:%S %p', target.x),
              } as HoverData);
            },
          },
        },
      },
    },
    chart: {
      events: {
        click: (): void => {
          history.push(K.ROUTE.EVENTS);
        },
        selection: (e: Highcharts.ChartSelectionContextObject): boolean => {
          if (e.xAxis) {
            window.location.href = getSearchPageLink(Math.floor(e.xAxis[0].min), Math.floor(e.xAxis[0].max));
          }
          return false;
        },
      },
    },
    series: [{ data }],
  };

  const options = merge(getBaseGraphConfig(), highchartsStyles, logVolumeGraphConfig);

  // Need to set the timezone option globally since we are using Highcharts.dateFormat()
  Highcharts.setOptions({
    global: {
      ...(timezone ? { timezoneOffset } : {}),
      // @ts-expect-error
      useUTC: !!timezone,
    },
  });

  const containerProps = {
    style: { height: '100%', width: '100%' },
    'data-test-id': ELEMENT.overviewLogVolumeGraph,
  };

  return (
    // Using onMouseOut over the container rather than HighCharts mouseOut event to work around the
    // event not firing
    // It seems like the event wasn't firing because we setHoverData on mouseOver which causes a
    // re-render and the mouseOut doesn't work

    // TODO: Remove eslint disable after implementation
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
    <div
      className="LogVolumeGraph"
      onMouseOut={(): void => {
        setHoverData({} as HoverData);
      }}
    >
      <TimelineLegend
        title="Total Log Volume"
        subtitle="(bytes/sec)"
        timeRange={{ startTime, endTime }}
        timeRangeText={hoverData.time}
        value={hoverData.value}
      />
      <div className="LogVolumeGraph__ChartContainer">
        <ScalyrHighcharts options={options} highcharts={Highcharts} containerProps={containerProps} />
      </div>
    </div>
  );
}

export default LogVolumeGraph;
